import { i18n } from '../utils/i18n';

interface DatePickerOptions {
  minDate: Date;
  maxDate: Date;
  minDuration: number;
}

interface DateSelection {
  minDate: string | null;
  maxDate: string | null;
  selectedDate: string | null;
  flexibility: number | null;
  isWholeMonth: boolean;
}

interface DatePickerState {
  selectedDate: string | null;
  minDate: string | null;
  maxDate: string | null;
  currentMonth: Date;
  mode: 'specific' | 'month';
  flexibility: number | null;
  isWholeMonth: boolean;
}

class DateModal {
  private readonly options: DatePickerOptions;
  private readonly state: DatePickerState;
  private modal: any = null;
  private container: HTMLElement | null = null;
  private confirmed = false;
  private stateSnapshot: DatePickerState | null = null;
  private readonly onConfirm?: (selected: DateSelection) => void;

  constructor(
    options: DatePickerOptions,
    onConfirm?: (selected: DateSelection) => void
  ) {
    this.options = options;
    this.onConfirm = onConfirm;
    this.state = {
      selectedDate: null,
      minDate: null,
      maxDate: null,
      currentMonth: this.getFirstSelectableMonth(),
      mode: 'specific',
      flexibility: null,
      isWholeMonth: false,
    };
  }

  public open(): void {
    // Save state snapshot so we can restore on cancel/dismiss
    this.confirmed = false;
    this.stateSnapshot = { ...this.state };

    const footer = this.createFooter();

    this.modal = new (window as any).Modal({
      title: i18n.t('modal.date.title'),
      content:
        '<div id="date-picker" class="flex flex-col items-center"></div>',
      footer: footer,
      className: 'search-component-date-picker',
      trackingLabel: 'searchComponent-date',
      fullHeight: true,
      onClose: () => {
        if (!this.confirmed && this.stateSnapshot) {
          this.state.selectedDate = this.stateSnapshot.selectedDate;
          this.state.minDate = this.stateSnapshot.minDate;
          this.state.maxDate = this.stateSnapshot.maxDate;
          this.state.flexibility = this.stateSnapshot.flexibility;
          this.state.isWholeMonth = this.stateSnapshot.isWholeMonth;
          this.state.mode = this.stateSnapshot.mode;
          this.state.currentMonth = this.stateSnapshot.currentMonth;
        }
        this.stateSnapshot = null;
      },
    }).open();

    if (this.modal) {
      const title = document.querySelector('.search-component-date-picker .modal-title') as HTMLElement;
      if (title) {
        title.dataset.testid = 'date-search-header';
      }
    }

    this.container = document.getElementById('date-picker');
    if (this.container) {
      this.render();
    }
  }

  public close(): void {
    this.modal?.close();
  }

  public getSelection(): DateSelection {
    return {
      minDate: this.state.minDate,
      maxDate: this.state.maxDate,
      selectedDate: this.state.selectedDate,
      flexibility: this.state.flexibility,
      isWholeMonth: this.state.isWholeMonth,
    };
  }

  public updateOptions(options: DatePickerOptions): void {
    this.options.minDate = options.minDate;
    this.options.maxDate = options.maxDate;
    this.options.minDuration = options.minDuration;

    if (this.container) {
      this.render();
    }
  }

  public setSelection(selection: DateSelection): void {
    this.state.minDate = selection.minDate;
    this.state.maxDate = selection.maxDate;
    this.state.selectedDate = selection.selectedDate;
    this.state.flexibility = selection.flexibility;
    this.state.isWholeMonth = selection.isWholeMonth;
    if (this.container) {
      this.render();
    }
  }

  /**
   * Restore a whole month (`YYYY-MM`) as a whole month. Returns false, and
   * selects nothing, when no day of the month can be departed on within the
   * window: a month still under way is kept; one that has ended before the
   * earliest departure, or opens after the latest, is not.
   */
  public restoreWholeMonth(month: string): boolean {
    const [year, monthNumber] = month.split('-').map(Number);
    if (!this.selectWholeMonth(year, monthNumber - 1)) return false;
    this.state.mode = 'month';
    this.state.currentMonth = new Date(year, monthNumber - 1);
    return true;
  }

  /**
   * Restore a date (`YYYY-MM-DD`) with its flexibility (null for an exact
   * date). Returns false, and selects nothing, when the calendar could not
   * offer that date: before the earliest departure, or too late to fit the
   * shortest stay before the latest.
   */
  public restoreDate(date: string, flexibility: number | null): boolean {
    const [year, month, day] = date.split('-').map(Number);
    if (this.isDayDisabled(new Date(year, month - 1, day))) return false;
    this.state.mode = 'specific';
    this.state.isWholeMonth = false;
    this.state.selectedDate = date;
    this.state.flexibility = flexibility;
    this.state.currentMonth = new Date(year, month - 1);
    this.updateMinMaxDates();
    return true;
  }

  private createFooter(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'flex gap-3 justify-around';

    const clearBtn = document.createElement('button');
    clearBtn.className = 'btn btn-secondary btn-lg grow';
    clearBtn.textContent = i18n.t('modal.common.clear');
    clearBtn.dataset.action = 'clear';
    clearBtn.dataset.testid = 'search-modal-clearall';
    clearBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.setSelection({
        minDate: null,
        maxDate: null,
        selectedDate: null,
        flexibility: null,
        isWholeMonth: false,
      });
      this.onConfirm?.(this.getSelection());
    });

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn btn-primary btn-lg grow';
    confirmBtn.textContent = i18n.t('modal.common.confirm');
    confirmBtn.dataset.action = 'confirm';
    confirmBtn.dataset.testid = 'search-modal-confirm';    
    confirmBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.onConfirm?.(this.getSelection());
      this.close();
    });

    container.appendChild(clearBtn);
    container.appendChild(confirmBtn);
    return container;
  }

  private render(): void {
    if (!this.container) return;

    const activeElement = document.activeElement as HTMLElement;
    const activeElementId = activeElement.id;
    const shouldRestoreFocus = this.container.contains(activeElement);

    this.container.innerHTML = '';

    const modeToggle = this.createModeToggle();
    this.container.appendChild(modeToggle);

    const mainContentContainer = document.createElement('div');
    mainContentContainer.className = 'flex flex-col w-full text-center';

    const dateTitle = document.createElement('h4');
    dateTitle.className = 'tt-heading-md text-center';

    mainContentContainer.appendChild(dateTitle);

    if (this.state.mode === 'specific') {
      dateTitle.classList.add('mb-3');
      dateTitle.textContent = i18n.t('modal.date.chooseDate');

      const navigation = this.createNavigation();
      mainContentContainer.appendChild(navigation);

      const separator = document.createElement('hr');
      separator.className = 'border-component mb-6';
      mainContentContainer.appendChild(separator);

      const calendar = this.createCalendar();
      mainContentContainer.appendChild(calendar);

      const flexSeparator = document.createElement('hr');
      flexSeparator.className = 'border-component mt-6 mb-4';
      mainContentContainer.appendChild(flexSeparator);

      const flexibility = this.createFlexibility();
      mainContentContainer.appendChild(flexibility);
    } else {
      mainContentContainer.classList.add('items-center');

      dateTitle.classList.add('mb-5');
      dateTitle.textContent = i18n.t('modal.date.chooseMonth');

      const calendar = this.createMonthList();
      mainContentContainer.appendChild(calendar);
    }

    this.container.appendChild(mainContentContainer);

    if (shouldRestoreFocus) {
      const newElement = this.container.querySelector(
        `#${activeElementId}`
      ) as HTMLElement;
      newElement?.focus();
    }
  }

  private createModeToggle(): HTMLElement {
    const container = document.createElement('div');
    container.className =
      'grid grid-cols-2 overflow-hidden p-1 pb-3 mb-3 max-w-80 w-full';

    const btnDefaultClasses =
      'py-2 rounded-none inline-flex items-center justify-center relative';

    const specificBtn = document.createElement('button');
    specificBtn.textContent = i18n.t('modal.date.specificDate');
    specificBtn.className = `${btnDefaultClasses} rounded-l-lg ${this.state.mode === 'specific' ? 'bg-functional tt-body-md-strong search-component__selected-mode' : 'bg-secondary-hover tt-body-md'}`;
    specificBtn.dataset.testid = 'date-mode-specific-date';
    specificBtn.addEventListener('click', () => {
      this.state.mode = 'specific';
      this.state.isWholeMonth = false;
      this.render();
    });

    const monthBtn = document.createElement('button');
    monthBtn.textContent = i18n.t('modal.date.wholeMonth');
    monthBtn.className = `${btnDefaultClasses} rounded-r-lg ${this.state.mode === 'month' ? 'bg-functional tt-body-md-strong search-component__selected-mode' : 'bg-secondary-hover tt-body-md'}`;
    monthBtn.dataset.testid = 'date-mode-full-month';
    monthBtn.addEventListener('click', () => {
      this.state.mode = 'month';
      this.render();
    });

    container.appendChild(specificBtn);
    container.appendChild(monthBtn);
    return container;
  }

  private createNavigation(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'flex justify-between mb-6';

    const currentYear = this.state.currentMonth.getFullYear();
    const currentMonthIndex = this.state.currentMonth.getMonth();

    const minMonthDate = new Date(
      this.options.minDate.getFullYear(),
      this.options.minDate.getMonth()
    );
    const maxMonthDate = new Date(
      this.options.maxDate.getFullYear(),
      this.options.maxDate.getMonth()
    );
    const currentMonthDate = new Date(currentYear, currentMonthIndex);

    const canGoPrev = currentMonthDate > minMonthDate;
    const canGoNext = currentMonthDate < maxMonthDate;

    const prevBtn = document.createElement('button');
    prevBtn.id = 'search-modal-date-prev-month';
    prevBtn.className =
      'btn btn-secondary border-0 btn-lg p-2 hover:bg-selected-surface';
    prevBtn.dataset.testid = 'prev-month-calendar-nav-button';
    prevBtn.disabled = !canGoPrev;
    prevBtn.addEventListener('click', () => {
      if (canGoPrev) {
        this.state.currentMonth = new Date(currentYear, currentMonthIndex - 1);
        this.render();
      }
    });

    const leftSvg = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg'
    );
    leftSvg.classList.add('icon');

    const leftSvgUse = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'use'
    );
    leftSvgUse.setAttribute('href', '#chevron-left');

    leftSvg.appendChild(leftSvgUse);
    prevBtn.appendChild(leftSvg);

    const monthSelect = document.createElement('select');
    monthSelect.className = 'form-select max-w-48 w-full';

    const getMonthName = (monthIndex: number) =>
      i18n.t(`modal.date.monthsFull.${monthIndex + 1}`);
    const minYear = this.options.minDate.getFullYear();
    const maxYear = this.options.maxDate.getFullYear();

    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month);
        if (
          date >=
            new Date(
              this.options.minDate.getFullYear(),
              this.options.minDate.getMonth()
            ) &&
          date <=
            new Date(
              this.options.maxDate.getFullYear(),
              this.options.maxDate.getMonth()
            ) &&
          this.hasSelectableDays(year, month)
        ) {
          const option = document.createElement('option');
          option.value = `${year}-${month + 1}`;
          option.dataset.testid = `select-month-select-${getMonthName(month).toLowerCase()}-${year}`
          option.textContent = `${getMonthName(month)} ${year}`;
          option.selected = year === currentYear && month === currentMonthIndex;
          monthSelect.appendChild(option);
        }
      }
    }

    monthSelect.addEventListener('change', e => {
      const [year, month] = (e.target as HTMLSelectElement).value.split('-');
      this.state.currentMonth = new Date(Number.parseInt(year), Number.parseInt(month) - 1);
      this.render();
    });

    const nextBtn = document.createElement('button');
    nextBtn.id = 'search-modal-date-next-month';
    nextBtn.className =
      'btn btn-secondary border-0 btn-lg p-2 hover:bg-selected-surface';
    nextBtn.dataset.testid = 'next-month-calendar-nav-button';
    nextBtn.disabled = !canGoNext;
    nextBtn.addEventListener('click', () => {
      if (canGoNext) {
        this.state.currentMonth = new Date(currentYear, currentMonthIndex + 1);
        this.render();
      }
    });

    const rightSvg = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg'
    );
    rightSvg.classList.add('icon');

    const rightSvgUse = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'use'
    );
    rightSvgUse.setAttribute('href', '#chevron-right');

    rightSvg.appendChild(rightSvgUse);
    nextBtn.appendChild(rightSvg);

    container.appendChild(prevBtn);
    container.appendChild(monthSelect);
    container.appendChild(nextBtn);
    return container;
  }

  private createCalendar(): HTMLElement {
    const container = document.createElement('div');
    container.className =
      'grid gap-1 grid-cols-7 text-center max-w-96 w-full self-center';

    const days = [
      i18n.t('modal.date.days.mon'),
      i18n.t('modal.date.days.tue'),
      i18n.t('modal.date.days.wed'),
      i18n.t('modal.date.days.thu'),
      i18n.t('modal.date.days.fri'),
      i18n.t('modal.date.days.sat'),
      i18n.t('modal.date.days.sun'),
    ];

    days.forEach(day => {
      const dayEl = document.createElement('div');
      dayEl.textContent = day;
      dayEl.className = 'tt-label-sm-strong mb-1';
      container.appendChild(dayEl);
    });

    const year = this.state.currentMonth.getFullYear();
    const month = this.state.currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let dayOfWeek = firstDay.getDay();
    dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    for (let i = 0; i < dayOfWeek; i++) {
      container.appendChild(document.createElement('div'));
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = this.formatDate(date);
      const isSelected = this.state.selectedDate === dateStr;
      const isDisabled = this.isDayDisabled(date);
      const isFlexible = this.isDateInFlexibilityRange(dateStr);

      const dayEl = document.createElement('button');
      dayEl.id = `search-modal-day-${day.toString()}`;
      dayEl.textContent = day.toString();

      const disabledClasses =
        'py-3 rounded tt-heading-md btn-secondary border hover:bg-selected-surface cursor-not-allowed';
      const selectedClasses = 'py-3 rounded tt-heading-md bg-functional';
      const regularClasses =
        'py-3 rounded tt-heading-md btn-secondary border hover:bg-selected-surface';
      const flexClasses =
        'py-3 rounded tt-heading-md bg-functional-tint border border-functional';

      if (isDisabled) {
        dayEl.className = disabledClasses;
      } else if (isSelected) {
        dayEl.className = selectedClasses;
        dayEl.dataset.testid = `calendar-isAvailable-isSelected-day-${day.toString()}`;
      } else if (isFlexible) {
        dayEl.className = flexClasses;
      } else {
        dayEl.className = regularClasses;
      }

      dayEl.disabled = isDisabled;

      if (!isDisabled) {
        dayEl.dataset.testid = `calendar-isAvailable-${isFlexible ? 'isHighlighted' : (isSelected ? 'isSelected' : 'select')}-day-${day.toString()}`;
        dayEl.addEventListener('click', () => {
          this.state.selectedDate = dateStr;
          this.state.isWholeMonth = false;
          dayEl.dataset.testid = `calendar-isAvailable-select-day-${day.toString()}`;
          this.updateMinMaxDates();
          this.render();
        });
      }

      container.appendChild(dayEl);
    }

    return container;
  }

  private createMonthList(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'max-w-96 w-full flex flex-col items-center';

    const months = [
      i18n.t('modal.date.months.jan'),
      i18n.t('modal.date.months.feb'),
      i18n.t('modal.date.months.mar'),
      i18n.t('modal.date.months.apr'),
      i18n.t('modal.date.months.may'),
      i18n.t('modal.date.months.jun'),
      i18n.t('modal.date.months.jul'),
      i18n.t('modal.date.months.aug'),
      i18n.t('modal.date.months.sep'),
      i18n.t('modal.date.months.oct'),
      i18n.t('modal.date.months.nov'),
      i18n.t('modal.date.months.dec'),
    ];
    const minYear = this.options.minDate.getFullYear();
    const maxYear = this.options.maxDate.getFullYear();
    const today = new Date();

    for (let year = minYear; year <= maxYear; year++) {
      const yearSection = document.createElement('div');
      const monthGrid = document.createElement('div');
      monthGrid.className = 'grid grid-cols-3 gap-2 max-w-80 w-full mb-6';

      let hasVisibleMonths = false;

      for (let month = 0; month < 12; month++) {
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        const isDisabled =
          lastDayOfMonth <= today ||
          firstDayOfMonth > this.options.maxDate ||
          lastDayOfMonth < this.options.minDate;

        if (isDisabled) continue;

        hasVisibleMonths = true;
        const isSelected = this.isMonthSelected(year, month);

        const defaultClasses = 'px-4 py-3 rounded tt-body-md-strong ';
        const deselectedClasses =
          'border btn-secondary hover:bg-selected-surface';
        const selectedClasses =
          'px-4 py-3 rounded tt-body-md-strong bg-functional';

        const monthBtn = document.createElement('button');
        monthBtn.textContent = `${months[month]} ${year.toString().slice(-2)}`;
        monthBtn.dataset.testid = `ps-month-select-${months[month].toLowerCase()}-${year.toString().slice(-2)}`;
        monthBtn.className = defaultClasses;

        if (isSelected) {
          monthBtn.className += ` ${selectedClasses}`;
        } else {
          monthBtn.className += ` ${deselectedClasses}`;
        }

        monthBtn.addEventListener('click', () => {
          this.selectWholeMonth(year, month);
          this.render();
        });

        monthGrid.appendChild(monthBtn);
      }

      if (hasVisibleMonths) {
        if (year > minYear) {
          const separator = document.createElement('hr');
          separator.className = 'w-full mb-6 border-component';
          container.appendChild(separator);
        }

        const yearLabel = document.createElement('div');
        yearLabel.textContent = year.toString();
        yearLabel.className = 'text-center tt-heading-xl mb-6';
        yearSection.appendChild(yearLabel);
        yearSection.appendChild(monthGrid);
        container.appendChild(yearSection);
      }
    }

    return container;
  }

  private createFlexibility(): HTMLElement {
    const container = document.createElement('div');
    container.className =
      'flex flex-col items-center w-full max-w-96 w-full self-center';

    const flexOptions = [{ value: 3 }];

    flexOptions.forEach(option => {
      const wrapper = document.createElement('div');
      wrapper.className = 'flex items-center gap-2';

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'form-checkbox';
      checkbox.dataset.testid = 'checkbox-flexi-date';
      checkbox.id = `flex-${option.value}`;
      checkbox.checked = this.state.flexibility === option.value;
      checkbox.addEventListener('change', () => {
        this.state.flexibility = checkbox.checked ? option.value : null;
        this.updateMinMaxDates();
        this.render();
      });

      const label = document.createElement('label');
      label.className =
        'ml-2 form-label mb-0 text-left tt-body-md cursor-pointer';
      label.dataset.testid = 'date-flexi-title';
      label.htmlFor = `flex-${option.value}`;

      const start = i18n.t('modal.date.flexibleDaysStart');
      const before = i18n.t('modal.date.flexibleDaysBefore', {
        days: option.value,
      });
      const joint = i18n.t('modal.date.flexibleDaysJoint');
      const after = i18n.t('modal.date.flexibleDaysAfter', {
        days: option.value,
      });
      const end = i18n.t('modal.date.flexibleDaysEnd');

      label.innerHTML = `${start}<strong>${before}</strong>${joint}<strong>${after}</strong>${end}`;

      wrapper.appendChild(checkbox);
      wrapper.appendChild(label);
      container.appendChild(wrapper);
    });

    return container;
  }

  /** Select the departable days of a month; false when it has none. */
  private selectWholeMonth(year: number, month: number): boolean {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      if (
        date > today &&
        date >= this.options.minDate &&
        date <= this.options.maxDate
      ) {
        if (!minDate) minDate = date;
        maxDate = date;
      }
    }

    if (minDate && maxDate) {
      this.state.selectedDate = null;
      this.state.minDate = this.formatDate(minDate);
      this.state.maxDate = this.formatDate(maxDate);
      this.state.isWholeMonth = true;
      this.state.flexibility = null;
      return true;
    }
    return false;
  }

  private isMonthSelected(year: number, month: number): boolean {
    if (!this.state.isWholeMonth || !this.state.minDate || !this.state.maxDate)
      return false;

    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let firstValidDay: Date | null = null;
    let lastValidDay: Date | null = null;

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      if (
        date > today &&
        date >= this.options.minDate &&
        date <= this.options.maxDate
      ) {
        if (!firstValidDay) firstValidDay = date;
        lastValidDay = date;
      }
    }

    return !!(
      firstValidDay &&
      lastValidDay &&
      this.formatDate(firstValidDay) === this.state.minDate &&
      this.formatDate(lastValidDay) === this.state.maxDate
    );
  }

  private updateMinMaxDates(): void {
    if (this.state.isWholeMonth) {
      return;
    }

    if (!this.state.selectedDate) {
      this.state.minDate = null;
      this.state.maxDate = null;
      return;
    }

    if (this.state.flexibility) {
      const [year, month, day] = this.state.selectedDate.split('-').map(Number);
      const selected = new Date(year, month - 1, day);

      const minDate = new Date(selected);
      minDate.setDate(minDate.getDate() - this.state.flexibility);

      const maxDate = new Date(selected);
      maxDate.setDate(maxDate.getDate() + this.state.flexibility);

      this.state.minDate = this.formatDate(minDate);
      this.state.maxDate = this.formatDate(maxDate);
    } else {
      this.state.minDate = this.state.selectedDate;
      this.state.maxDate = this.state.selectedDate;
    }
  }

  private hasSelectableDays(year: number, month: number): boolean {
    const lastDay = new Date(year, month + 1, 0);
    for (let day = 1; day <= lastDay.getDate(); day++) {
      if (!this.isDayDisabled(new Date(year, month, day))) return true;
    }
    return false;
  }

  /**
   * Whether the calendar refuses `date` as a departure: in the past, before
   * the earliest departure, or too late to fit the shortest stay before the
   * latest.
   */
  private isDayDisabled(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxDate = new Date(this.options.maxDate);
    maxDate.setDate(maxDate.getDate() - this.options.minDuration);

    return date < today || date < this.options.minDate || date > maxDate;
  }

  private getFirstSelectableMonth(): Date {
    const minYear = this.options.minDate.getFullYear();
    const maxYear = this.options.maxDate.getFullYear();

    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 0; month < 12; month++) {
        const monthDate = new Date(year, month);
        if (
          monthDate >= new Date(this.options.minDate.getFullYear(), this.options.minDate.getMonth()) &&
          monthDate <= new Date(this.options.maxDate.getFullYear(), this.options.maxDate.getMonth()) &&
          this.hasSelectableDays(year, month)
        ) {
          return new Date(year, month);
        }
      }
    }
    return new Date();
  }

  private isDateInFlexibilityRange(dateStr: string): boolean {
    if (
      !this.state.flexibility ||
      !this.state.selectedDate ||
      !this.state.minDate ||
      !this.state.maxDate
    ) {
      return false;
    }
    return (
      dateStr >= this.state.minDate &&
      dateStr <= this.state.maxDate &&
      dateStr !== this.state.selectedDate
    );
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}

export default DateModal;
